import type { Question, Section } from "./data"
import { QUESTIONS } from "./questions"
import { rerender } from "./render"
import { dueScore, seenThisSession, selectBlueprinted } from "./selection"
import { type Mode, state } from "./state"
import { recordAnswer } from "./storage"
import { shuffle } from "./util"

// ============================================================
// QUIZ SCREEN STATE
// ============================================================

export const quizState = {
  screen: null as "quiz" | "results" | null,
}

// ============================================================
// QUIZ FLOW
// ============================================================

// Selects the question set for a mode. This is the only randomized step in
// starting a quiz; everything downstream runs off the ordered list it returns.
// User-facing test modes draw only from verified questions. Unverified and
// discarded entries never appear in a quiz.
function selectForMode(mode: Mode, section?: Section): Question[] {
  const active = QUESTIONS.filter((q) => q.status === "verified")

  if (mode === "study") {
    // Study Quiz: the blueprint shapes the section mix while the due
    // score ranks within each section, prioritizing unseen questions,
    // then miss rate and recency, then dropping mastered to near zero.
    return selectBlueprinted(active, 8, dueScore)
  }
  if (mode === "practice-short") return selectBlueprinted(active, 15)
  if (mode === "practice-full") return selectBlueprinted(active, 25)
  if (mode === "section") {
    const inSection = active.filter((q) => q.answer.section === section)
    return selectBlueprinted(inSection, inSection.length)
  }
  return []
}

// Initializes and shows the quiz from an explicit, ordered question list.
// `startQuiz` feeds this the randomized selection. A caller that already knows
// the exact set, such as the snapshot harness, feeds a fixed list for a fully
// deterministic run that does not depend on the size of the question bank.
export function beginQuiz(mode: Mode, set: Question[], section?: Section) {
  state.currentMode = mode
  state.currentSection = section || null

  state.allQuestions = set
  state.queue = state.allQuestions.map((_, i) => i)
  state.selections = {}
  state.results = {}
  state.wasSkipped = {}
  state.confirmed = false
  // Shuffle option order once per question, stable for this run. Prevents
  // position-memorization without breaking selection preservation across skips.
  state.shuffledChoices = {}
  state.allQuestions.forEach((q, i) => {
    state.shuffledChoices[i] = shuffle([q.answer.text, ...q.otherChoices])
  })

  quizState.screen = "quiz"
  const home = document.getElementById("home")
  if (home) home.style.display = "none"
  window.scrollTo({ top: 0, behavior: "smooth" })
  rerender()
}

export function startQuiz(mode: Mode, section?: Section) {
  beginQuiz(mode, selectForMode(mode, section), section)
}

// Starts a quiz from a known set of question ids, in the given order, skipping
// any id not present in the bank. The randomized modes reach the same setup by
// way of `startQuiz`; this entry lets a caller supply the ids directly.
export function startQuizFromIds(mode: Mode, ids: string[]) {
  const byId = new Map(QUESTIONS.map((q) => [q.id, q]))
  const set: Question[] = []
  for (const id of ids) {
    const q = byId.get(id)
    if (q) set.push(q)
  }
  beginQuiz(mode, set)
}

export function selectAnswer(i: number) {
  if (state.confirmed) return // answer already locked in; this shouldn't fire
  const qIdx = state.queue[0]
  state.selections[qIdx] = i
  rerender()
}

export function primaryAction() {
  if (state.confirmed) {
    nextQuestion()
  } else {
    confirmAnswer()
  }
}

function confirmAnswer() {
  const qIdx = state.queue[0]
  if (!(qIdx in state.selections)) return
  const q = state.allQuestions[qIdx]
  const options = state.shuffledChoices[qIdx]
  const given = state.selections[qIdx]
  const givenText = options[given]
  const correct = givenText === q.answer.text
  state.results[qIdx] = { given, givenText, correct }
  state.confirmed = true

  // Capture this answer in long-term history. Per-question-version event log;
  // doesn't care which mode we're in. Persisted in the background.
  recordAnswer(q, correct)
  // Mark as seen-this-session so the selector won't immediately re-pick it
  // if the user kicks off another test.
  seenThisSession.add(q.id)

  rerender()
}

export function skipQuestion() {
  if (state.confirmed) return // can't skip an already-confirmed question
  if (state.queue.length <= 1) return // nothing to defer to
  const qIdx = state.queue.shift()
  if (qIdx === undefined) return
  state.queue.push(qIdx)
  state.wasSkipped[qIdx] = true
  // state.selections[qIdx] persists untouched — will be restored when the question cycles back
  rerender()
}

function nextQuestion() {
  // Only called after confirming. Pop the confirmed question off the queue.
  state.queue.shift()
  state.confirmed = false
  if (state.queue.length === 0) {
    quizState.screen = "results"
    rerender()
    window.scrollTo({ top: 0, behavior: "smooth" })
  } else {
    rerender()
    window.scrollTo({ top: 0, behavior: "smooth" })
  }
}

export function backHome() {
  quizState.screen = null
  document.getElementById("verify")?.classList.remove("active")
  document.getElementById("explore")?.classList.remove("active")
  const home = document.getElementById("home")
  if (home) home.style.display = "block"
  rerender()
  window.scrollTo({ top: 0, behavior: "smooth" })
}
